/**
 * ShareButton Component
 * 
 * A component that enables users to share their JSON data through generated URLs.
 * Handles uploading JSON to cloud storage and generating shareable links.
 */

import { getApiEndpoint } from '../config/api-config.js';

export class ShareButton {
    constructor(container, options = {}) {
        this.container = container
        this.options = {
            onShareSuccess: options.onShareSuccess || (() => { }),
            onShareError: options.onShareError || (() => { }),
            onLoadingChange: options.onLoadingChange || (() => { }),
            ...options
        }

        // Set API endpoint after options are merged
        if (!this.options.apiEndpoint) {
            this.options.apiEndpoint = this.getApiEndpoint()
        }

        this.button = null
        this.isSharing = false
        this.shareModal = null

        this.init()
    }

    /**
     * Get the appropriate API endpoint based on environment
     */
    getApiEndpoint() {
        // Allow override via options for testing
        if (this.options && this.options.apiEndpoint) {
            return this.options.apiEndpoint
        }

        // Use the dynamic API configuration
        return getApiEndpoint()
    }

    init() {
        this.render()
        this.setupEventListeners()
    }

    render() {
        // Create share button
        this.button = document.createElement('button')
        this.button.type = 'button'
        this.button.className = 'btn btn-primary share-btn'
        this.button.innerHTML = `
            <span class="icon">📤</span>
            <span class="btn-text">Share JSON</span>
        `
        this.button.title = 'Share JSON data via URL'
        this.button.disabled = false

        // Add to container
        this.container.appendChild(this.button)
    }

    setupEventListeners() {
        this.button.addEventListener('click', this.handleShareClick.bind(this))
    }

    /**
     * Handle share button click
     */
    async handleShareClick() {
        if (this.isSharing) return

        try {
            // Get JSON data from the application
            const jsonData = this.getJSONData()

            if (!jsonData) {
                this.showError('No JSON data to share. Please enter valid JSON first.')
                return
            }

            // Validate JSON before sharing
            if (!this.validateJSON(jsonData)) {
                this.showError('Invalid JSON data. Please fix errors before sharing.')
                return
            }

            // Start sharing process
            this.setLoadingState(true)

            const shareResult = await this.shareJSON(jsonData)

            if (shareResult.success) {
                this.showShareModal(shareResult.shareUrl, shareResult.expiresAt)
                this.options.onShareSuccess(shareResult)
                this.dispatchEvent('shareSuccess', shareResult)
            } else {
                this.showError(shareResult.error || 'Failed to share JSON data')
                this.options.onShareError(shareResult)
                this.dispatchEvent('shareError', shareResult)
            }

        } catch (error) {
            console.error('Share error:', error)
            this.showError('An unexpected error occurred while sharing')
            this.options.onShareError({ error: error.message })
            this.dispatchEvent('shareError', { error: error.message })
        } finally {
            this.setLoadingState(false)
        }
    }

    /**
     * Get JSON data from the main application
     */
    getJSONData() {
        // Access the global app instance to get current JSON
        if (window.JSONViewerApp && window.JSONViewerApp.getJSONValue) {
            const jsonString = window.JSONViewerApp.getJSONValue()
            return jsonString.trim()
        }

        // Fallback: try to get from textarea directly
        const textarea = document.querySelector('.json-input')
        if (textarea) {
            return textarea.value.trim()
        }

        return null
    }

    /**
     * Validate JSON data before sharing
     */
    validateJSON(jsonString) {
        if (!jsonString) return false

        try {
            JSON.parse(jsonString)
            return true
        } catch (error) {
            return false
        }
    }

    /**
     * Share JSON data via API
     */
    async shareJSON(jsonData) {
        const requestBody = {
            jsonData: jsonData,
            metadata: {
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            }
        }

        try {
            const response = await fetch(this.options.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`)
            }

            const result = await response.json()
            return result

        } catch (error) {
            console.error('API request failed:', error)

            // Provide helpful error messages based on the error type
            let errorMessage = error.message || 'Failed to share JSON data'

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage = 'Unable to connect to the sharing service. The API may not be deployed yet or there may be a network issue.'
            } else if (error.message.includes('CORS')) {
                errorMessage = 'Cross-origin request blocked. The API may need CORS configuration.'
            }

            // Return a structured error response
            return {
                success: false,
                error: errorMessage
            }
        }
    }

    /**
     * Set loading state
     */
    setLoadingState(isLoading) {
        this.isSharing = isLoading
        this.button.disabled = isLoading

        if (isLoading) {
            this.button.innerHTML = `
                <span class="icon loading-spinner">⟳</span>
                <span class="btn-text">Sharing...</span>
            `
            this.button.classList.add('loading')
        } else {
            this.button.innerHTML = `
                <span class="icon">🔗</span>
                <span class="btn-text">Share</span>
            `
            this.button.classList.remove('loading')
        }

        this.options.onLoadingChange(isLoading)
        this.dispatchEvent('loadingChange', { isLoading })
    }

    /**
     * Show share modal with URL
     */
    showShareModal(shareUrl, expiresAt) {
        // Remove existing modal if any
        this.hideShareModal()

        // Create modal
        this.shareModal = document.createElement('div')
        this.shareModal.className = 'share-modal-overlay'
        this.shareModal.innerHTML = `
            <div class="share-modal">
                <div class="share-modal-header">
                    <h3>Share JSON</h3>
                    <button type="button" class="share-modal-close" aria-label="Close">✕</button>
                </div>
                <div class="share-modal-content">
                    <p>Your JSON has been shared! Copy the link below:</p>
                    <div class="share-url-container">
                        <input type="text" class="share-url-input" value="${shareUrl}" readonly>
                        <button type="button" class="copy-url-btn">Copy</button>
                    </div>
                    <div class="share-info">
                        <p class="expiration-info">
                            <span class="icon">⏰</span>
                            Link expires: ${this.formatExpirationTime(expiresAt)}
                        </p>
                    </div>
                </div>
            </div>
        `

        // Add to document
        document.body.appendChild(this.shareModal)

        // Setup modal event listeners
        this.setupModalEventListeners()

        // Focus on URL input for easy copying
        const urlInput = this.shareModal.querySelector('.share-url-input')
        urlInput.focus()
        urlInput.select()

        this.dispatchEvent('modalShow', { shareUrl, expiresAt })
    }

    /**
     * Setup modal event listeners
     */
    setupModalEventListeners() {
        if (!this.shareModal) return

        const closeBtn = this.shareModal.querySelector('.share-modal-close')
        const copyBtn = this.shareModal.querySelector('.copy-url-btn')
        const urlInput = this.shareModal.querySelector('.share-url-input')

        // Close modal
        closeBtn.addEventListener('click', () => this.hideShareModal())
        this.shareModal.addEventListener('click', (e) => {
            if (e.target === this.shareModal) {
                this.hideShareModal()
            }
        })

        // Copy URL
        copyBtn.addEventListener('click', () => this.copyToClipboard(urlInput.value))

        // Select all on focus
        urlInput.addEventListener('focus', () => urlInput.select())

        // Close on escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                this.hideShareModal()
                document.removeEventListener('keydown', handleEscape)
            }
        }
        document.addEventListener('keydown', handleEscape)
    }

    /**
     * Hide share modal
     */
    hideShareModal() {
        if (this.shareModal) {
            this.shareModal.remove()
            this.shareModal = null
            this.dispatchEvent('modalHide')
        }
    }

    /**
     * Copy text to clipboard
     */
    async copyToClipboard(text) {
        try {
            await navigator.clipboard.writeText(text)
            this.showCopyFeedback(true)
        } catch (error) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            try {
                document.execCommand('copy')
                this.showCopyFeedback(true)
            } catch (err) {
                console.error('Failed to copy:', err)
                this.showCopyFeedback(false)
            }

            document.body.removeChild(textArea)
        }
    }

    /**
     * Show copy feedback
     */
    showCopyFeedback(success) {
        const copyBtn = this.shareModal?.querySelector('.copy-url-btn')
        if (!copyBtn) return

        const originalText = copyBtn.textContent
        copyBtn.textContent = success ? 'Copied!' : 'Failed'
        copyBtn.classList.add(success ? 'success' : 'error')

        setTimeout(() => {
            copyBtn.textContent = originalText
            copyBtn.classList.remove('success', 'error')
        }, 2000)

        this.dispatchEvent('copyFeedback', { success })
    }

    /**
     * Format expiration time for display
     */
    formatExpirationTime(expiresAt) {
        if (!expiresAt) return 'in 24 hours'

        try {
            const expireDate = new Date(expiresAt)

            // Check if date is valid
            if (isNaN(expireDate.getTime())) {
                return 'in 24 hours'
            }

            const now = new Date()
            const diffMs = expireDate.getTime() - now.getTime()
            const diffHours = Math.ceil(diffMs / (1000 * 60 * 60))

            if (diffHours <= 0) {
                return 'expired'
            } else if (diffHours === 1) {
                return 'in 1 hour'
            } else if (diffHours < 24) {
                return `in ${diffHours} hours`
            } else {
                return expireDate.toLocaleDateString() + ' at ' + expireDate.toLocaleTimeString()
            }
        } catch (error) {
            return 'in 24 hours'
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        // Create error notification
        const errorNotification = document.createElement('div')
        errorNotification.className = 'share-error-notification'
        errorNotification.innerHTML = `
            <div class="error-content">
                <span class="error-icon">⚠️</span>
                <span class="error-message">${message}</span>
                <button type="button" class="error-close">✕</button>
            </div>
        `

        // Add to document
        document.body.appendChild(errorNotification)

        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (errorNotification.parentNode) {
                errorNotification.remove()
            }
        }, 5000)

        // Manual close
        const closeBtn = errorNotification.querySelector('.error-close')
        closeBtn.addEventListener('click', () => errorNotification.remove())

        this.dispatchEvent('error', { message })
    }

    /**
     * Check if sharing is currently in progress
     */
    isLoading() {
        return this.isSharing
    }

    /**
     * Enable or disable the share button
     */
    setEnabled(enabled) {
        if (this.button) {
            this.button.disabled = !enabled || this.isSharing
        }
    }

    /**
     * Update the API endpoint
     */
    setApiEndpoint(endpoint) {
        this.options.apiEndpoint = endpoint
    }

    /**
     * Event system
     */
    dispatchEvent(type, detail = {}) {
        const event = new CustomEvent(`shareButton:${type}`, {
            detail: { ...detail, component: this },
            bubbles: true
        })
        this.container.dispatchEvent(event)
    }

    /**
     * Cleanup
     */
    destroy() {
        this.hideShareModal()

        if (this.button) {
            this.button.removeEventListener('click', this.handleShareClick)
            this.button.remove()
        }

        // Remove any error notifications
        const errorNotifications = document.querySelectorAll('.share-error-notification')
        errorNotifications.forEach(notification => notification.remove())
    }
}